import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { TeamEntity } from '@shared/models/team.models';
import { AppState } from '@app/store/state/app.state';
import { failureNotification, successNotification } from '@shared/store/notification.duck';
import { redirectTo } from '@shared/store/auth.duck';
import {
  createTeamEventRequest,
  deleteTeamEventRequest,
  getTeamEvents,
  requestTeamActivitiesFor,
  requestWorkGroupActivitiesFor,
  updateTeamEventRequest
} from '@features/wall-planner/WallPlannerService';
import { CreateTeamOptions, TeamEvent } from '@features/wall-planner/wall-planner.models';
import { dedup } from '@shared/utils/ArrayUtils';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { Tag } from '@shared/models/tags.models';

export interface WallPlannerState {
  loading: boolean;
  favouredTeamsEdited: boolean;
  workGroups: Tag[];
  teamProjects: TeamEvent[];
  teamDetails: any; // TeamActivities
  workGroupDetails: any; // TeamActivities
}

export const initialWallPlannerState: WallPlannerState = {
  loading: false,
  favouredTeamsEdited: false,
  workGroups: [],
  teamProjects: [],
  teamDetails: {},
  workGroupDetails: {},
}

export const createTeamEvent: any = createAsyncThunk(
  'wallPlanner/createTeamEvent',
  async (createTeamOptions: CreateTeamOptions, thunkAPI) => {
    try {
      if (createTeamOptions.id) {
        await updateTeamEventRequest(createTeamOptions);
        thunkAPI.dispatch(successNotification('Team event updated'));
      } else {
        await createTeamEventRequest(createTeamOptions);
        thunkAPI.dispatch(successNotification('Team event added'));
      }
      thunkAPI.dispatch(loadTeamEvents({teamId: createTeamOptions.teamId}))
      thunkAPI.dispatch(redirectTo('/whereabouts/work-planner'));
    } catch (err: any) {
      thunkAPI.dispatch(failureNotification(err?.message ?? 'Failed to add team event'));
    }
  }
);

export const deleteTeamEvent: any = createAsyncThunk(
  'wallPlanner/deleteTeamEvent',
  async (request: any, thunkAPI) => {
    try {
      await deleteTeamEventRequest(request.teamEventId);
      thunkAPI.dispatch(successNotification('Team event deleted'));
      thunkAPI.dispatch(loadTeamEvents({teamId: request.teamId}))
      thunkAPI.dispatch(redirectTo('/whereabouts/work-planner'));
    } catch (err: any) {
      console.log(err)
      thunkAPI.dispatch(failureNotification('Failed to delete team event'));
    }
  }
);

export const loadTeamEvents: any = createAsyncThunk(
  'wallPlanner/loadTeamEvents',
  async (params: {teamId: string, date: string}, thunkAPI) => {
    const {teamId, date} = params;
    if (!teamId) {
      return [];
    }
    const state = thunkAPI.getState() as AppState;
    const dateString = date || (state.dashboard.calendarDate.format(DATE_FORMAT))

    // Race condition and deduping issue here
    const response = await getTeamEvents(teamId, dateString);
    return response;
  }
);

export const loadWallPlannerTeamsActivities: any = createAsyncThunk(
  'wallPlanner/loadWallPlannerTeamsActivities',
  async (team: TeamEntity, thunkAPI) => {
    await new Promise((resolve: any) => setTimeout(() => resolve(), Math.random() * 1000 * 0.5));
    const state = thunkAPI.getState() as AppState
    const whereaboutsOptions = state.whereaboutsOptions.whereaboutsOptions;
    const startDate = state.dashboard.calendarDate.clone().startOf('month');
    return await requestTeamActivitiesFor(whereaboutsOptions, team.id, startDate.format(DATE_FORMAT));
  }
);

// Refetches every visible group's activities for the visible month. Used after
// bulk whereabouts writes (week-scope editing): dispatchWhereaboutsSaved fans
// saved days out to the whereabouts slices but not to this one. Covers the
// followed groups plus, when a whereabouts page is pinned to one team or
// workgroup, that group as well.
export const refreshWallPlannerActivities: any = createAsyncThunk(
  'wallPlanner/refreshWallPlannerActivities',
  async (params: void, thunkAPI) => {
    const state = thunkAPI.getState() as AppState;
    const followedTeams: TeamEntity[] = state.followingTeamsAndTags.teams ?? [];
    const followedWorkGroups: Tag[] = state.followingTeamsAndTags.workGroups ?? [];
    followedTeams.forEach((team: TeamEntity) =>
      thunkAPI.dispatch(loadWallPlannerTeamsActivities(team)));
    followedWorkGroups.forEach((workGroup: Tag) =>
      thunkAPI.dispatch(loadWallPlannerWorkGroupActivities(workGroup)));

    const scope = state.teamMovements?.scope;
    if (scope?.kind === 'team' && !followedTeams.some(t => t.id === scope.teamId)) {
      thunkAPI.dispatch(loadWallPlannerTeamsActivities({ id: scope.teamId } as TeamEntity));
    }
    if (scope?.kind === 'workgroup' && !followedWorkGroups.some(w => w.id === scope.workGroupId)) {
      thunkAPI.dispatch(loadWallPlannerWorkGroupActivities({ id: scope.workGroupId } as Tag));
    }
  }
);

export const loadWallPlannerWorkGroupActivities: any = createAsyncThunk(
  'wallPlanner/loadWallPlannerWorkGroupActivities',
  async (workGroup: Tag, thunkAPI) => {
    await new Promise((resolve: any) => setTimeout(() => resolve(), Math.random() * 1000 * 0.5));
    const state = thunkAPI.getState() as AppState
    const whereaboutsOptions = state.whereaboutsOptions.whereaboutsOptions;
    const startDate = state.dashboard.calendarDate.clone().startOf('month').day(1);
    return await requestWorkGroupActivitiesFor(whereaboutsOptions, workGroup.id, startDate.format(DATE_FORMAT));
  }
);

const wallPlannerSlice = createSlice({
  name: 'wallPlanner',
  initialState: initialWallPlannerState,
  reducers: {
    reset: () => initialWallPlannerState,
    dirtyFavouredTeams: (state) => ({...state, favouredTeamsEdited: true}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTeamEvent.pending, (state) => ({...state, loading: true,}))
      .addCase(createTeamEvent.rejected, (state) => ({...state, loading: false,}))
      .addCase(createTeamEvent.fulfilled, (state) => ({...state, loading: false,}))
      .addCase(loadTeamEvents.pending, (state) => ({...state, teamProjects: []}))
      .addCase(loadTeamEvents.fulfilled, (state, action) => ({
      ...state,
      teamProjects: dedup([ ...state.teamProjects, ...action.payload, ], (p1: TeamEvent, p2: TeamEvent) => p1.id === p2.id)
    }))
      .addCase(loadWallPlannerTeamsActivities.fulfilled, (state, action) => {
      if (!action.payload || !action.payload.teamId || !action.payload.teamActivities) {
        return state;
      }
      const updatedTeamDetails = {
        ...state.teamDetails,
        [action.payload.teamId]: {whereabouts: action.payload.teamActivities, restricted: action.payload.restricted},
      }
      return {
        ...state,
        teamDetails: updatedTeamDetails
      }
    })
      .addCase(loadWallPlannerWorkGroupActivities.fulfilled, (state, action) => {
      if (!action.payload || !action.payload.workGroupId || !action.payload.teamActivities) {
        return state;
      }
      const updatedTeamDetails = {
        ...state.workGroupDetails,
        [action.payload.workGroupId]: {whereabouts: action.payload.teamActivities, restricted: action.payload.restricted},
      }
      return {
        ...state,
        workGroupDetails: updatedTeamDetails
      }
    });
  }
});


export default wallPlannerSlice.reducer;

// Selectors
export const selectTeamDetails = (state: AppState) => state.wallPlanner.teamDetails;
export const selectWorkGroupDetails = (state: AppState) => state.wallPlanner.workGroupDetails;
export const selectTeamProjects = (state: AppState) => state.wallPlanner.teamProjects;
export const selectIsLoading = (state: AppState) => state.wallPlanner.loading;
